import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { MaterialModule } from 'src/app/shared/material.module';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { AppState } from 'src/app/state/app.reducer';
import { selectSelectableProjects } from 'src/app/state/app.selectors';
import { ClassificationService } from '../../work-classification.service';
import { ClassificationPreviewComponent } from '../classification-preview/classification-preview.component';
import { mapTemplateError } from '../template-error';
import { PreviewNode, projectTreeToPreview } from '../template-tree';

export interface CopyFromProjectDialogData {
  targetProjectId: number;
}

/** "Kopioi työmaalta": copies another site's tree into the (empty) current site. Closes with true on success. */
@Component({
  selector: 'app-copy-from-project-dialog',
  templateUrl: './copy-from-project-dialog.component.html',
  styleUrls: ['./copy-from-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe, AsyncPipe, ClassificationPreviewComponent],
})
export class CopyFromProjectDialogComponent {
  private data = inject<CopyFromProjectDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<CopyFromProjectDialogComponent, boolean>>(MatDialogRef);
  private classificationService = inject(ClassificationService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private store = inject<Store<AppState>>(Store);
  private destroyRef = inject(DestroyRef);

  /** The current site is filtered out, so SAME_PROJECT cannot be chosen. */
  projects$ = this.store
    .select(selectSelectableProjects)
    .pipe(map(list => list.filter(p => p.id !== this.data.targetProjectId)));

  sourceId = signal<number | null>(null);
  previewNodes = signal<PreviewNode[]>([]);
  previewLoading = signal(false);
  previewLoaded = signal(false);
  saving = signal(false);

  selectSource(id: number): void {
    this.sourceId.set(id);
    this.previewNodes.set([]);
    this.previewLoaded.set(false);
    this.previewLoading.set(true);
    this.classificationService
      .getClassifications(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: tree => {
          if (this.sourceId() !== id) {
            return;
          }
          this.previewNodes.set(projectTreeToPreview(tree));
          this.previewLoading.set(false);
          this.previewLoaded.set(true);
        },
        error: err => {
          this.previewLoading.set(false);
          this.logger.errorWithContext('CopyFromProjectDialog', 'Failed to load source tree', err);
          const m = mapTemplateError(err);
          this.snackBar.error(this.translate.instant(m.key, m.params));
        },
      });
  }

  get canCopy(): boolean {
    return this.sourceId() != null && this.previewNodes().length > 0 && !this.saving();
  }

  copy(): void {
    const source = this.sourceId();
    if (source == null || !this.canCopy) {
      return;
    }
    this.saving.set(true);
    this.classificationService
      .copyClassifications(source, this.data.targetProjectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.dialogRef.close(true);
        },
        error: err => {
          this.saving.set(false);
          const m = mapTemplateError(err);
          this.snackBar.error(this.translate.instant(m.key, m.params));
        },
      });
  }
}
